import { Button, Divider, Grid, IconButton, Typography } from '@mui/material';
import React, { useState } from 'react';
import {
  getPropertyById,
  PermissionsEnum,
  PropertyInput,
  deleteProperty as deletePropertyMutation,
  getProperties,
  MIXPANEL_EVENTS,
  Configuration,
  CONFIGURATION_TYPE,
} from 'vendor-marketplace-shared-library';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import InLineEditTextField from '../../components/common/InLineEditTextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import WorkOrderContainerModule from './components/WorkOrderContainerModule';
import PropertyUsersModule from './components/PropertyUsersModule';
import { updateProperty as updatePropertyMutation } from 'vendor-marketplace-shared-library';
import PropertyFormModal from '../../components/property/PropertyFormModal';
import useAuth from '../../hooks/useAuth';
import DeleteConfirmationModal from '../../components/common/DeleteConfirmationModal';
import useAlert from '../../hooks/useAlert';
import { PROPERTY_DELETE_ERROR } from '../../error-messages';
import useMixpanel from '../../hooks/useMixpanel';
import { PropertyAssetsContainer } from './components/PropertyAssetsContainer';
import useConfigurations from '../../hooks/useConfigurations';
import { getConfigurationValue } from '../../utilities/helpers';

export function PropertyDetailsPage() {
  const { validateUserPermissions } = useAuth();
  const params = useParams();
  const alert = useAlert();
  const propertyId = params['id'];
  const navigate = useNavigate();
  const { mixpanelTrack } = useMixpanel();
  const { configurationsQuery } = useConfigurations();
  const [openFormModal, setOpenFormModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const { data, refetch } = useQuery(getPropertyById, {
    variables: { id: propertyId },
  });

  const [updateProperty] = useMutation(updatePropertyMutation, {
    refetchQueries: [getPropertyById],
    onCompleted: () => {
      setOpenFormModal(false);
      mixpanelTrack(MIXPANEL_EVENTS.ORGANIZATION_SETTINGS_PROPERTY_UPDATED);
    },
  });

  const [deleteProperty] = useMutation(deletePropertyMutation, {
    refetchQueries: [getProperties],
    variables: {
      id: propertyId,
    },
    onError: () => {
      alert.setAlert({
        message: PROPERTY_DELETE_ERROR,
      });
    },
    onCompleted: () => {
      setOpenDeleteModal(false);
      navigate('/properties');
      mixpanelTrack(MIXPANEL_EVENTS.ORGANIZATION_SETTINGS_PROPERTY_DELETED);
    },
  });

  const onUpdate = async (formData: Partial<PropertyInput>) => {
    await updateProperty({
      variables: {
        id: propertyId,
        input: formData,
      },
    });
  };

  const userWarrantyProductIsEnabledConfig = configurationsQuery?.data?.getConfigurations?.find(
    (configuration: Configuration) =>
      configuration.name === CONFIGURATION_TYPE.WARRANTY_PRODUCT_IS_ENABLED,
  );

  const isUserWarrantyProductIsEnabled = getConfigurationValue(userWarrantyProductIsEnabledConfig);

  return (
    <Grid container direction={'column'} sx={{ rowGap: 2 }}>
      <Grid container spacing={1}>
        {/*Back Button*/}
        <Grid item>
          <IconButton onClick={() => navigate('/properties')}>
            <ArrowBackIcon />
          </IconButton>
        </Grid>
        <Grid item container>
          {/*Details*/}
          <Grid item>
            {validateUserPermissions([PermissionsEnum.PROPERTIES_UPDATE]) ? (
              <InLineEditTextField
                textVariant='h4'
                content={data?.getPropertyById.name}
                onSave={(value: string) => {
                  onUpdate({ name: value });
                }}
              />
            ) : (
              <Typography variant={'h4'}>{data?.getPropertyById.name}</Typography>
            )}
          </Grid>
          {/*Address*/}
          <Grid item container sx={styles.centeredContainer}>
            <Grid item>
              <Typography color={'secondary.main'} variant={'h6'}>
                {data?.getPropertyById.fullAddress}
              </Typography>
            </Grid>
            {validateUserPermissions([PermissionsEnum.PROPERTIES_UPDATE]) && (
              <Grid item>
                <IconButton onClick={() => setOpenFormModal(true)}>
                  <ModeEditIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Divider />

      <Grid item container spacing={2} sx={{ paddingTop: 2 }}>
        {/*Users Container*/}
        <Grid item xs={12} md={4}>
          <PropertyUsersModule property={data?.getPropertyById ?? {}} />
        </Grid>

        {/*Work Orders Container*/}
        <Grid item xs={12} md={4}>
          <WorkOrderContainerModule
            property={data?.getPropertyById ?? {}}
            refetchProperty={refetch}
          />
        </Grid>
        {/*Assets Container*/}
        {isUserWarrantyProductIsEnabled && (
          <Grid item xs={12} md={4}>
            <PropertyAssetsContainer assets={data?.getPropertyById?.assets ?? []} />
          </Grid>
        )}
      </Grid>
      <Grid item>
        <Button onClick={() => setOpenDeleteModal(true)} color='error'>
          Delete property
        </Button>
      </Grid>
      {openFormModal && (
        <PropertyFormModal
          open={openFormModal}
          setOpen={setOpenFormModal}
          onSubmit={onUpdate}
          property={data?.getPropertyById ?? {}}
        />
      )}
      {openDeleteModal && (
        <DeleteConfirmationModal
          entity={'property'}
          open={openDeleteModal}
          setOpen={setOpenDeleteModal}
          onDelete={deleteProperty}
        />
      )}
    </Grid>
  );
}

const styles = {
  centeredContainer: {
    alignItems: 'center',
  },
  centeredApart: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
};
